import { useState } from 'react';
import { Button, makeStyles } from '@material-ui/core';

import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';
import { MIN_SEARCH_LENGTH } from '../../../shared/constants/search.constants';
import { EntityStatusFilter, toEntityListFilters } from '../../../shared/constants/entity-status-filter.constants';
import { useAdminGetSegments } from '../../hooks/admin-get-segments.hook';
import { useCreateSegment } from '../../hooks/create-segment.hook';
import { ALL_SEGMENT_CATEGORIES, SegmentCategoryFilter } from '../../constants/segment-category.constants';
import { SegmentSortColumn, SegmentSortDirection } from '../../types/segment.type';
import { CreateSegmentRequestDTO } from '../../types/dtos/create-segment.dto';
import { SegmentsListFilters } from '../../components/Containers/SegmentsListFilters.component';
import { SegmentsTable } from '../../components/Tables/SegmentsTable.component';
import { CreateSegmentDialog } from '../../components/Modals/CreateSegmentDialog.component';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },

  createButton: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function AdminSegmentListView() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<SegmentCategoryFilter>(ALL_SEGMENT_CATEGORIES);
  const [statusFilter, setStatusFilter] = useState<EntityStatusFilter>('all');
  const [sortBy, setSortBy] = useState<SegmentSortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SegmentSortDirection>('asc');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 400).trim();

  const { isLoading, isRefetching, totalCount, segments, refetch } = useAdminGetSegments({
    page: page + 1,
    limit: rowsPerPage,
    sortBy,
    sortDirection,
    filters: {
      ...(categoryFilter !== ALL_SEGMENT_CATEGORIES ? { category: categoryFilter } : {}),
      ...(debouncedSearch.length >= MIN_SEARCH_LENGTH ? { name: debouncedSearch } : {}),
      ...toEntityListFilters(statusFilter),
    },
  });
  const { createSegment, isSubmitting: isCreating } = useCreateSegment();

  const withPageReset =
    <Value,>(setValue: (value: Value) => void) =>
    (value: Value) => {
      setValue(value);
      setPage(0);
    };

  const handleSortChange = (column: SegmentSortColumn) => {
    setSortDirection(column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortBy(column);
    setPage(0);
  };

  const handleCreateSegment = async (data: CreateSegmentRequestDTO) => {
    const createdSegment = await createSegment(data);
    if (!createdSegment) return;

    setIsCreateDialogOpen(false);
    await refetch();
  };

  return (
    <section className={classes.root}>
      <h2>Segments</h2>
      <div className={classes.toolbar}>
        <SegmentsListFilters
          search={search}
          categoryFilter={categoryFilter}
          statusFilter={statusFilter}
          onSearchChange={withPageReset(setSearch)}
          onCategoryFilterChange={withPageReset(setCategoryFilter)}
          onStatusFilterChange={withPageReset(setStatusFilter)}
        />
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          Créer un segment
        </Button>
      </div>
      <SegmentsTable
        segments={segments}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading || isRefetching}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onPageChange={setPage}
        onRowsPerPageChange={withPageReset(setRowsPerPage)}
        onSortChange={handleSortChange}
      />
      <CreateSegmentDialog
        open={isCreateDialogOpen}
        isSubmitting={isCreating}
        onSubmit={handleCreateSegment}
        onClose={() => setIsCreateDialogOpen(false)}
      />
    </section>
  );
}
